import { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useDebouncedCallback } from 'use-debounce';
import * as Sentry from '@sentry/browser';
import WorklogsList from '@chronos/components/WorklogsList';
import Spinner from '@chronos/components/Spinner';
import useWorklogsList from '@chronos/ui-toolkit/hooks/useWorklogsList';
import eventEmitter from '@chronos/utils/eventEmitter';
import {
  selectOnlineUsers,
  setHeaderTitle,
  selectLiveWorklogsByType,
  saveLiveWorklog,
} from '@chronos/slices/boardSlice';
import {
  useGetUsersQuery,
  initialUserState,
  useTrelloMembers,
} from '@chronos/slices/usersApi';
import useWorklogsFilter from '@chronos/ui-toolkit/hooks/useWorklogsFilter';
import { useBoardContext } from '@chronos/ui-toolkit/contexts';
import {
  useGetTrelloBoardCardsQuery,
  initialCardState,
  useTrelloCards,
} from '@chronos/slices/cardsApi';
import { useSaveBoardMemberSettingsMutation } from '@chronos/slices/settingsApi';
import Button from '@chronos/components/Button';
import { subDays } from 'date-fns';
import { LoadMoreButton } from '@chronos/components/styled/styled-global';
import { trelloCardFields } from '../../constants';
import { setLabelExpanded, selectIsLabelExpanded } from '../../slices/appSlice';
import * as GLOBAL_S from '../../styled/styled-global';
import * as S from './styled';
import { openModal } from '../../slices/modalSlice';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { cardId } = useParams();
  const boardContext = useBoardContext();
  const { organizationId, boardId, memberId, boardMemberSettings } =
    boardContext;
  const navigate = useNavigate();
  const location = useLocation();
  const liveWorklogsCount = useRef(0);
  const isMobile = true;

  const boardLiveWorklogs = useSelector((state) =>
    selectLiveWorklogsByType(state, { roomType: 'board' })
  );
  const onlineUsers = useSelector(selectOnlineUsers);
  const isLabelExpanded = useSelector(selectIsLabelExpanded);

  const isTimerRunning = useMemo(
    () =>
      boardLiveWorklogs.some(
        (worklog) =>
          ['run', 'pause'].includes(worklog.timerStatus) &&
          worklog.memberId === memberId
      ),
    [boardLiveWorklogs, memberId]
  );

  useEffect(() => {
    dispatch(setHeaderTitle('Work Log'));
  }, []);

  const [saveSettings] = useSaveBoardMemberSettingsMutation();

  const {
    data: { ids: usersIds, entities: usersEntities } = initialUserState,
  } = useGetUsersQuery({
    boardId,
    organizationId,
  });

  const {
    data: { ids: cardsIds, entities: cardsEntities } = initialCardState,
  } = useGetTrelloBoardCardsQuery({
    boardId,
    filter: 'visible',
    fields: trelloCardFields,
  });
  const [fetchOutTrelloCards] = useTrelloCards({
    boardId,
    filter: 'visible',
    fields: trelloCardFields,
  });
  const [filter, { onChangeFilter, requestParams }] = useWorklogsFilter({
    cardId: boardContext.cardId,
    boardId,
    memberId,
    isAllowToSeeOthers:
      boardContext.boardMemberPermissions?.isAllowToSeeOthers || false,
    weekStartsOn: boardContext.boardAdminSettings?.weekStartsOn || 0,
    usersIds,
    usersEntities,
    cardsIds,
    cardsEntities,
  });
  const [fetchOutTrelloMembers] = useTrelloMembers({
    boardId,
    organizationId: boardContext.belongToOrganization
      ? boardContext.organizationId
      : null,
  });
  const [
    { state: worklogsState, listRef, infiniteLoaderRef, autosizerRef },
    {
      fetchWorklogs,
      fetchWorklogsMeta,
      reloadList,
      clearList,
      addNewDummyWorklog,
      isItemLoaded,
    },
  ] = useWorklogsList({
    memberId,
    cardId,
    requestParams,
    filter,
    usersEntities,
    fetchOutTrelloMembers,
    cardsEntities,
    fetchOutTrelloCards,
    liveWorklogs: boardLiveWorklogs,
    showLiveWorklogs: boardMemberSettings?.showLiveWorklogs || false,
    showOwnLiveWorklogs: boardMemberSettings?.showOwnLiveWorklogs || false,
    allowedToSeeOthersWorklog:
      boardContext.boardMemberPermissions?.isAllowToSeeOthers || false,
    weekStartsOn: boardContext.boardAdminSettings?.weekStartsOn || 0,
    defaultWorklogsCount: 15,
  });

  useEffect(() => {
    /*
     * Only if worklogs length become less than before means most probably a new worklog
     * has been appeared and it makes sense run fetchWorklogsMeta()
     */
    const becomeLess = liveWorklogsCount.current > boardLiveWorklogs.length;
    if (becomeLess) {
      liveWorklogsCount.current = boardLiveWorklogs.length;
      fetchWorklogsMeta();
    } else {
      liveWorklogsCount.current = boardLiveWorklogs.length;
    }
  }, [boardLiveWorklogs.length]);

  useEffect(() => {
    fetchWorklogsMeta({
      shouldMarkAsLoading: true,
      shouldReloadListIfChange: true,
    });
  }, [boardId]);

  useEffect(() => {
    eventEmitter.on('updateState', fetchWorklogsMeta);
    return () => {
      eventEmitter.off('updateState', fetchWorklogsMeta);
    };
  }, [fetchWorklogsMeta, addNewDummyWorklog]);

  const handleSavedWorklog = useCallback(async () => {
    await fetchWorklogsMeta({
      shouldMarkAsLoading: false,
      shouldDelayBeforePerform: false,
      shouldReloadListIfChange: false,
    });
    await reloadList();
  }, [dispatch, fetchWorklogsMeta]);

  const handleSavingWorklog = useCallback(
    async ({ values }) => {
      if (values.cardId === cardId) {
        addNewDummyWorklog();
      }
    },
    [cardId, addNewDummyWorklog]
  );

  useEffect(() => {
    eventEmitter.on('savingWorklog', handleSavingWorklog);
    eventEmitter.on('savedWorklog', handleSavedWorklog);
    return () => {
      eventEmitter.off('savingWorklog', handleSavingWorklog);
      eventEmitter.off('savedWorklog', handleSavedWorklog);
    };
  }, [handleSavingWorklog, handleSavedWorklog]);

  const onStartWorklog = useCallback(
    async (worklog) => {
      try {
        if (boardMemberSettings.showStartReview) {
          dispatch(
            openModal({
              name: 'WorklogFormModal',
              meta: {
                mode: 'startReview',
                worklog: { seconds: 0 },
                title: 'Worklog Review',
                cardId: worklog.cardId,
                boardId: worklog.boardId,
                memberId: worklog.memberId,
              },
            })
          );
        } else {
          const state = {
            boardId,
            cardId: worklog.cardId,
            memberId,
            startTime: Date.now(),
            pauseTrackedSeconds: 0,
            timerStatus: 'run',
            trackingWorklogComment: '',
            organizationId: boardContext.organizationId,
          };
          dispatch(
            saveLiveWorklog({
              cardId: worklog.cardId,
              boardId,
              state,
            })
          );
        }
      } catch (err) {
        console.error(err);
        Sentry.captureException(err);
      }
    },
    [worklogsState, boardMemberSettings]
  );

  const onHideOwnLiveWorklog = useCallback(
    () =>
      saveSettings({
        body: {
          boardId,
          showOwnLiveWorklogs: false,
        },
      }),
    [boardId]
  );

  const worklogsCount = worklogsState.listItemsCount;
  const worklogsListProps = {
    worklogs: worklogsState.worklogs,
    listItems: worklogsState.listItems,
    dayPoints: worklogsState.dayPoints,
    weekPoints: worklogsState.weekPoints,
    cards: cardsEntities,
    users: usersEntities,
    onlineUsers,
    worklogsCount,
    totalTime: worklogsState.totalTimeStr,
    loadMoreWorklogs: fetchWorklogs,
    isItemLoaded,
    infiniteLoaderRef,
    autosizerRef,
    listRef,
    showWorklogButtonsDefault: false,
    showTimeline: true,
    shouldDisplayCard: true,
    isWorklogsListHidden: false,
    shouldDisplayDivider: false,
    isShowScreenshotsBtn: true,
    isShowEditBtn: false,
    isShowDeleteBtn: false,
    isShowLabel: true,
    isLabelExpanded,
    showStartBtn: !isTimerRunning,
    isMobile,
    cardNameTextDecoration: 'auto',
    cardNamePlacement: 'body',
    popoverPlacement: 'below',
    commentPlacement: 'body',
    onHideOwnLiveWorklog,
    onEditWorklog: () => {},
    onDeleteWorklog: () => {},
    onStartWorklog,
    onClickCardname: (item) => {
      if (item?.id) {
        const state = { backgroundLocation: location };
        navigate(`cards/${item.id}`, { state });
      }
    },
    onClickExpandLabel: () => {
      dispatch(setLabelExpanded(!isLabelExpanded));
    },
  };

  const [isShowLoadMoreBtn, setIsLoadMoreButtonVisible] = useState(false);
  const setShowLoadMoreButton = useDebouncedCallback(
    setIsLoadMoreButtonVisible,
    100
  );
  const handleLoadMoreClick = () => {
    const {
      date: { ranges },
    } = filter;
    if (ranges.length === 1) {
      const startDate = subDays(ranges[0].startDate, 31);
      onChangeFilter('date', { endDate: ranges[0].endDate, startDate });
    }
  };

  if (worklogsState.isLoading) {
    return null;
  }

  if (worklogsCount === 0) {
    return (
      <S.NotFoundMessage>
        {`The board doesn't have worklogs yet`}
      </S.NotFoundMessage>
    );
  }

  return (
    <>
      <PullToRefresh
        refreshingContent={<Spinner />}
        pullingContent={null}
        onRefresh={async () => {
          clearList();
          await fetchWorklogsMeta({
            shouldMarkAsLoading: false,
            shouldDelayBeforePerform: false,
            shouldReloadListIfChange: false,
          });
        }}
      >
        <GLOBAL_S.WorklogsListContainer>
          <WorklogsList
            showTimeline
            setShowLoadMoreButton={setShowLoadMoreButton}
            {...worklogsListProps}
          />
        </GLOBAL_S.WorklogsListContainer>
      </PullToRefresh>
      <LoadMoreButton position={isShowLoadMoreBtn ? 'top' : 'bottom'}>
        <Button onClick={handleLoadMoreClick}>Load more</Button>
      </LoadMoreButton>
    </>
  );
};

export default Dashboard;
